import { ChangeEvent, useEffect, useState } from "react";
import { FieldConfig } from "../../../types";
import { fetchFieldConfig, upsertFieldConfig } from "../../../api";

import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { DraggableColumn } from "../TableConfigurator";
import CustomModal from "./CustomModal";

interface FieldConfiguratorProps {
  objectName: string;
  onClose: (fieldConfigUpdate?: boolean) => void;
  onSave: (fieldConfigs: FieldConfig[], save: boolean) => void;
  pushFieldConfog?: (fieldConfigs: FieldConfig[]) => void;
  toggleOpen?: boolean;
  showAddFieldOption?: boolean;
}

export const AddFieldConfigurator: React.FC<FieldConfiguratorProps> = ({ objectName, onSave, onClose, pushFieldConfog }) => {
  const [fieldConfig, setFieldConfig] = useState<FieldConfig>(new FieldConfig({ object: objectName, isVisible: true, isDeleted: false }))
  const [fieldKeyManuallyEdited, setFieldKeyManuallyEdited] = useState(false)

  const toFieldKey = (label: string) =>
    label.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '')

  const getFieldConfig = async () => {
    try {
      const fieldConfigResult = await fetchFieldConfig(objectName)
      if (pushFieldConfog) pushFieldConfog(fieldConfigResult)
      return fieldConfigResult
    } catch (error) {
      console.error('Error fetching Field Configs', error);
      return []
    }
  }

  function handleAddCustomFields(event: ChangeEvent<HTMLInputElement>): void {
    const { name, value } = event.target;
    if (name === 'fieldName') {
      setFieldKeyManuallyEdited(true)
      setFieldConfig(prev => new FieldConfig({ ...prev, fieldName: value }))
    } else if (name === 'fieldHeading') {
      setFieldConfig(prev => new FieldConfig({
        ...prev,
        fieldHeading: value,
        fieldName: fieldKeyManuallyEdited ? prev.fieldName : toFieldKey(value),
      }))
    } else {
      setFieldConfig(prev => new FieldConfig({ ...prev, [name]: value }))
    }
  }

  async function updateFieldConfig(): Promise<void> {
    if (!fieldConfig?.fieldHeading?.trim()) {
      toast.error('Field Label is required.'.toTranslatedDisplay());
      return;
    }
    if (!fieldConfig?.fieldName?.trim()) {
      toast.error('Field Key is required.'.toTranslatedDisplay());
      return;
    }
    const fieldConfigs = await getFieldConfig()
    if (fieldConfigs.some(fieldConfigValue => fieldConfigValue.fieldName === fieldConfig?.fieldName)) {
      toast.error('Field name already exists. Please choose a different name.'.toTranslatedDisplay());
    } else {
      try {
        const newConfig = [...fieldConfigs, fieldConfig]
        await upsertFieldConfig(newConfig)
        toast.success('Field added successfully.'.toTranslatedDisplay());
        if (pushFieldConfog) pushFieldConfog(newConfig)
        onClose(true)
      } catch (error) {
        toast.error('Failed to save custom field. Please try again.'.toTranslatedDisplay());
      }
    }
  }

  return (
    <div className='mb-3'>
      <div className="form-group">
        <label className='form-label'>{'Field Label'.toTranslatedDisplay()}</label>
        <input
          type={'text'}
          id='fieldHeading'
          name='fieldHeading'
          placeholder={'Display label (e.g. GST Number)'.toTranslatedDisplay()}
          className="form-control mb-3"
          value={fieldConfig.fieldHeading || ''}
          onChange={handleAddCustomFields}
        />
      </div>
      <button type='button' className='btn btn-primary' onClick={updateFieldConfig}>{'Save'.toTranslatedDisplay()}</button>
      <button type='button' className='btn btn-secondary ms-2' onClick={() => onClose(false)}>{'Cancel'.toTranslatedDisplay()}</button>
    </div>
  )
}

export const EditFieldConfigurator: React.FC<FieldConfiguratorProps> = ({ objectName, onSave, onClose, showAddFieldOption = false, pushFieldConfog, toggleOpen = false }) => {
  const [fieldConfigs, setFieldConfigs] = useState<FieldConfig[]>([])
  const getFieldConfig = async () => {
    try {
      const fieldConfigResult = await fetchFieldConfig(objectName)
      if (pushFieldConfog) pushFieldConfog(fieldConfigResult)
      setFieldConfigs(fieldConfigResult)
    } catch (error) {
      console.error('Error fetching Field Configs', error);
    }
  }
  useEffect(() => {
    getFieldConfig()
  }, [toggleOpen])
  function moveColumn(dragIndex: number, hoverIndex: number): void {
    const updatedColumns = [...fieldConfigs];
    const [draggedColumn] = updatedColumns.splice(dragIndex, 1);
    updatedColumns.splice(hoverIndex, 0, draggedColumn);

    updatedColumns.forEach((column, index) => {
      column.sortOrder = index;
    });

    setFieldConfigs(updatedColumns);
  }

  function handleColumnChange(fieldName: string, isVisible: boolean): void {
    setFieldConfigs(prevConfigs =>
      prevConfigs.map(config =>
        config.fieldName === fieldName
          ? { ...config, isVisible: isVisible }
          : config
      )
    );
  }

  const handleSave = async () => {
    try {
      const updateFieldConfigResult = await upsertFieldConfig(fieldConfigs)
      toast.success('Field saved successfully!'.toTranslatedDisplay());
      await getFieldConfig()
      onClose(true)
    } catch (error) {
      toast.error('Unknown error try again!'.toTranslatedDisplay());
    }
  }

  function handleDeleteColumn(fieldName: string): void {
    setFieldConfigs((prevFieldConfigs) =>
      prevFieldConfigs.map((fieldConfig) =>
        fieldConfig.fieldName === fieldName
          ? { ...fieldConfig, isDeleted: true }
          : fieldConfig
      )
    );
  }

  const [showAddFieldModal, setShowAddFieldModal] = useState(false)
  function handleAddFieldModalToggle(): void {
    setShowAddFieldModal(!showAddFieldModal)
  }
  function handleAddFieldModalClose(): void {
    setShowAddFieldModal(false)
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <div>
        <span className="badge badge-light-info text-wrap lh-sm text-start">{'You can drag the order of columns to your requirement!'.toTranslatedDisplay()}</span>
        {fieldConfigs.map((config, index) => (
          <DraggableColumn
            key={config.fieldName as string}
            showDeleteOption={true}
            index={index}
            config={config}
            moveColumn={moveColumn}
            handleColumnChange={handleColumnChange}
            handleDeleteColumn={handleDeleteColumn}
          />
        ))}
        {showAddFieldOption &&
          <>
            <div>
              <a href="#" onClick={handleAddFieldModalToggle} className="btn btn-sm btn-outline btn-outline-dashed btn-outline-primary btn-active-light-primary">{'Add Custom Fields'.toTranslatedDisplay()}</a>
            </div>

          </>
        }
        <button className='btn btn-sm btn-primary mt-2' onClick={handleSave}>{'Save'.toTranslatedDisplay()}</button>
      </div>
      <CustomModal
        title={'Add Custom Field'.toTranslatedDisplay()}
        isVisible={showAddFieldModal}
        onClose={handleAddFieldModalClose}
        body={<AddFieldConfigurator objectName={objectName} onSave={function (fieldConfigs: FieldConfig[], save: boolean): void {
          throw new Error('Function not implemented.');
        }} onClose={function (fieldConfigUpdate?: boolean | undefined): void {
          handleAddFieldModalClose()
          if (fieldConfigUpdate) getFieldConfig()
        }} />}
        size="small"
        id="addCustomFieldModal"
      />
    </DndProvider>
  )
}